import { useEffect, useState } from 'react';

const formatDate = (value) => {
    if (!value) return '';
    return new Date(value).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
};

const formatPaymentStatus = (status) => {
    switch (status) {
        case 'paid':
            return 'Payé';
        case 'partial':
            return 'Partiel';
        case 'unpaid':
            return 'Impayé';
        case 'pending':
            return 'En attente';
        default:
            return status || '—';
    }
};

export default function SalesHistoryTab() {
    const [orders, setOrders] = useState({ data: [], current_page: 1, last_page: 1 });
    const [dishTotals, setDishTotals] = useState([]);
    const [statusFilter, setStatusFilter] = useState('');
    const [paymentFilter, setPaymentFilter] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const loadSalesHistory = async (page = 1) => {
        setIsLoading(true);
        setError('');

        const params = new URLSearchParams();
        if (statusFilter) params.append('status', statusFilter);
        if (paymentFilter) params.append('payment_status', paymentFilter);
        if (dateFilter) params.append('date', dateFilter);
        params.append('page', page);

        try {
            const response = await fetch(`/api/orders/sales-history?${params.toString()}`, {
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!response.ok) {
                const payload = await response.json().catch(() => null);
                throw new Error(payload?.message || 'Impossible de charger l’historique de ventes');
            }

            const payload = await response.json();
            setOrders(payload.orders || { data: [], current_page: 1, last_page: 1 });
            setDishTotals(payload.dishTotals || []);
        } catch (err) {
            console.error(err);
            setError(err.message || 'Erreur lors du chargement des données');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadSalesHistory(1);
    }, [statusFilter, paymentFilter, dateFilter]);

    return (
        <div className="space-y-6">
            <div className="rounded-3xl border border-green-200 bg-white p-6 shadow-sm">
                <h2 className="text-3xl font-semibold text-green-900">Historique des ventes</h2>
                <p className="mt-2 text-sm text-green-600">
                    Suivez les commandes, leur statut, le montant payé et les ventes par plat.
                </p>

                <div className="mt-6 grid gap-4 md:grid-cols-3">
                    <label className="block">
                        <span className="text-sm text-slate-600">Date</span>
                        <input
                            type="date"
                            value={dateFilter}
                            onChange={(event) => setDateFilter(event.target.value)}
                            className="mt-2 w-full rounded-3xl border border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-900 focus:border-green-500 focus:outline-none"
                        />
                    </label>

                    <label className="block">
                        <span className="text-sm text-slate-600">Statut commande</span>
                        <select
                            value={statusFilter}
                            onChange={(event) => setStatusFilter(event.target.value)}
                            className="mt-2 w-full rounded-3xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 focus:border-green-500 focus:outline-none"
                        >
                            <option value="">Tous</option>
                            <option value="pending">En attente</option>
                            <option value="preparing">En préparation</option>
                            <option value="ready">Prête</option>
                            <option value="served">Servie</option>
                            <option value="completed">Terminée</option>
                            <option value="cancelled">Annulée</option>
                        </select>
                    </label>

                    <label className="block">
                        <span className="text-sm text-slate-600">Statut paiement</span>
                        <select
                            value={paymentFilter}
                            onChange={(event) => setPaymentFilter(event.target.value)}
                            className="mt-2 w-full rounded-3xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 focus:border-green-500 focus:outline-none"
                        >
                            <option value="">Tous</option>
                            <option value="unpaid">Impayé</option>
                            <option value="partial">Partiel</option>
                            <option value="paid">Payé</option>
                        </select>
                    </label>
                </div>
            </div>

            {error && (
                <div className="rounded-3xl border border-rose-200 bg-rose-50 p-4 text-rose-700">
                    {error}
                </div>
            )}

            <div className="grid gap-6 xl:grid-cols-[2fr_1fr]">
                <div className="rounded-3xl border border-green-200 bg-white p-6 shadow-sm">
                    <h3 className="text-xl font-semibold text-green-900">Commandes</h3>
                    <div className="mt-4 overflow-x-auto">
                        <table className="w-full text-left text-sm text-slate-700">
                            <thead>
                                <tr className="border-b border-slate-200 text-slate-900">
                                    <th className="py-3 pr-4">Commande</th>
                                    <th className="py-3 pr-4">Table</th>
                                    <th className="py-3 pr-4">Statut</th>
                                    <th className="py-3 pr-4">Paiement</th>
                                    <th className="py-3 pr-4">Montant</th>
                                    <th className="py-3 pr-4">Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan="6" className="py-8 text-center text-slate-500">
                                            Chargement...
                                        </td>
                                    </tr>
                                ) : orders.data.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="py-8 text-center text-slate-500">
                                            Aucune commande pour ces filtres.
                                        </td>
                                    </tr>
                                ) : (
                                    orders.data.map((order) => (
                                        <tr key={order.id} className="border-b border-slate-100">
                                            <td className="py-4 pr-4 font-semibold text-slate-900">{order.order_number || `#${order.id}`}</td>
                                            <td className="py-4 pr-4">{order.table?.name || 'À emporter'}</td>
                                            <td className="py-4 pr-4 capitalize text-slate-700">{order.status || '—'}</td>
                                            <td className="py-4 pr-4 capitalize text-slate-700">{formatPaymentStatus(order.payment_status)}</td>
                                            <td className="py-4 pr-4 font-semibold text-green-900">Ar {Number(order.total || 0).toFixed(2)}</td>
                                            <td className="py-4 pr-4 text-slate-600">{formatDate(order.created_at || order.ordered_at)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
                        <div>{orders.data.length} commande(s) affichée(s)</div>
                        <div className="flex items-center gap-2">
                            <button
                                disabled={orders.current_page <= 1 || isLoading}
                                onClick={() => loadSalesHistory(Math.max(1, orders.current_page - 1))}
                                className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >Précédent</button>
                            <button
                                disabled={orders.current_page >= orders.last_page || isLoading}
                                onClick={() => loadSalesHistory(Math.min(orders.last_page, orders.current_page + 1))}
                                className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >Suivant</button>
                        </div>
                    </div>
                </div>

                <div className="rounded-3xl border border-green-200 bg-white p-6 shadow-sm">
                    <h3 className="text-xl font-semibold text-green-900">Ventes par plat</h3>
                    <div className="mt-4 space-y-3">
                        {dishTotals.length === 0 ? (
                            <p className="text-sm text-slate-600">Aucune vente enregistrée sur cette période.</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-sm text-slate-700">
                                    <thead>
                                        <tr className="border-b border-slate-200 text-slate-900">
                                            <th className="py-3 pr-4">Plat</th>
                                            <th className="py-3 pr-4">Quantité</th>
                                            <th className="py-3 pr-4">Chiffre</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dishTotals.map((dish) => (
                                            <tr key={dish.id} className="border-b border-slate-100">
                                                <td className="py-3 pr-4">{dish.name}</td>
                                                <td className="py-3 pr-4">{dish.quantity}</td>
                                                <td className="py-3 pr-4 font-semibold text-slate-900">Ar {Number(dish.total).toFixed(2)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
